// ---------------------------------------------------------------------------
// MapEmbed Renderer (editor key: MapEmbed)
// ---------------------------------------------------------------------------
// Static, scriptless map embed for the published page. The editor renders an
// interactive React preview; on publish we emit a sandboxed Google Maps
// iframe pointed at a public search URL so we never need an API key or
// JavaScript runtime. Falls back to a labeled placeholder if no address is
// supplied. Address text is shown below the map when `showAddress = "yes"`.

import { registerRenderer } from "../registry.js";
import type { RenderContext } from "../types.js";
import { attr, text, url } from "../escape.js";

interface MapEmbedProps {
  address?: string;
  heading?: string;
  height?: string;
  showAddress?: "yes" | "no";
}

const DEFAULT_HEIGHT = "300px";
const MAX_ADDRESS_LENGTH = 500;

function normalizeHeight(value: unknown): string {
  if (typeof value !== "string") return DEFAULT_HEIGHT;
  const trimmed = value.trim();
  if (!trimmed) return DEFAULT_HEIGHT;
  // Accept px/% with positive integer; reject anything else to stay safe in
  // inline style attribute.
  if (/^\d{1,5}(px|%)$/.test(trimmed)) return trimmed;
  return DEFAULT_HEIGHT;
}

function buildEmbedUrl(address: string): string {
  const safe = address.slice(0, MAX_ADDRESS_LENGTH);
  const encoded = encodeURIComponent(safe);
  return `https://www.google.com/maps?q=${encoded}&output=embed`;
}

function render(props: Record<string, unknown>, _ctx: RenderContext): string {
  const p = props as MapEmbedProps;

  const address = typeof p.address === "string" ? p.address.trim() : "";
  const heading = typeof p.heading === "string" ? p.heading.trim() : "";
  const height = normalizeHeight(p.height);
  const showAddress = p.showAddress !== "no" && address.length > 0;

  const headingHtml = heading
    ? `\n  <h2 class="ease-map-embed__heading">${text(heading)}</h2>`
    : "";

  if (!address) {
    return `<section class="ease-section ease-map-embed">${headingHtml}
  <div class="ease-map-embed__placeholder" role="img" aria-label="Map location not configured" style="height:${attr(height)}"></div>
</section>`;
  }

  const embedUrl = buildEmbedUrl(address);
  const addressHtml = showAddress
    ? `\n  <p class="ease-map-embed__address">${text(address)}</p>`
    : "";
  const title = heading || "Location map";

  return `<section class="ease-section ease-map-embed">${headingHtml}
  <div class="ease-map-embed__frame" style="position:relative;width:100%;height:${attr(height)};overflow:hidden;border-radius:12px">
    <iframe src="${url(embedUrl)}" title="${attr(title)}" loading="lazy" referrerpolicy="no-referrer-when-downgrade" sandbox="allow-scripts allow-same-origin allow-popups" style="position:absolute;inset:0;width:100%;height:100%;border:0"></iframe>
  </div>${addressHtml}
</section>`;
}

registerRenderer("map-embed", { render });
